'use client';

import { useState, useEffect } from 'react';
import ProjectCard from './ProjectCard';
import FilterBar from './FilterBar';
import CaseStudyModal from './CaseStudyModal';
import Icon from '@/components/ui/AppIcon';


interface Project {
  id: number;
  title: string;
  category: string;
  year: number;
  image: string;
  alt: string;
  description: string;
  client: string;
  challenge: string;
  solution: string;
  results?: string[];
  additionalImages?: Array<{url: string;alt: string;}>;
}

const PortfolioInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeFilter, setActiveFilter] = useState('All Products');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories = [
  'All Products',
  'Structural',
  'Electrical',
  'Power',
  'Cabs & Gates'];


  const projects: Project[] = [
  {
    id: 1,
    title: 'Structural System Parts',
    category: 'Structural',
    year: 2024,
    image: "/assets/images/steel_frame_structure_line_art.png",
    alt: '',
    description:
    'Complete brand transformation for a premium cosmetics line, establishing a sophisticated visual identity that resonates with discerning consumers.',
    client: 'Luxe Beauty International',
    challenge:
    'The client needed to reposition their brand in the ultra-premium segment while maintaining recognition among existing customers. The challenge was creating a visual identity that felt both timeless and contemporary, appealing to a younger demographic without alienating their established base.',
    solution:
    'We developed a refined visual system centered on bold typography, monochromatic color palette, and geometric precision. The new identity strips away unnecessary ornamentation, allowing the product quality to speak for itself through clean, confident design language.',
    results: [
    '47% increase in brand recognition',
    '32% growth in premium segment sales',
    'Featured in 5 major design publications'],

    additionalImages: [
    {
      url: "https://images.unsplash.com/photo-1696603972001-71f026e5dcc4",
      alt: 'Close-up of luxury cosmetics product line showing consistent minimalist branding across multiple items'
    },
    {
      url: "https://images.unsplash.com/photo-1658157146592-b530d4070229",
      alt: 'Brand identity collateral including business cards and stationery with black and white design'
    }]

  },
  {
    id: 2,
    title: 'Electrical System Parts',
    category: 'Electrical',
    year: 2024,
    image: "/assets/images/electrical_component_line_art.png",
    alt: '',
    description:
    'Digital portfolio platform for an award-winning architecture firm, showcasing their projects through immersive visual storytelling.',
    client: 'Studio Architects',
    challenge:
    'Creating a digital experience that matches the sophistication of their architectural work while ensuring fast load times and seamless navigation across thousands of high-resolution project images.',
    solution:
    'We built a custom Next.js platform with advanced image optimization, implementing a masonry grid layout that adapts fluidly to different screen sizes. The interface prioritizes the work itself, with navigation that appears only when needed.',
    results: [
    '85% increase in project inquiries',
    '2.3x longer average session duration',
    'Winner of Awwwards Site of the Day'],

    additionalImages: [
    {
      url: "https://images.unsplash.com/photo-1684242282290-997cabc3ad06",
      alt: 'Desktop and mobile views of architecture portfolio website showing responsive design'
    }]

  },
  {
    id: 3,
    title: 'Power System Parts',
    category: 'Power',
    year: 2023,
    image: "/assets/images/Power_System-1763931993992.png",
    alt: '',
    description:
    'Art direction and design for a 24-page fashion editorial exploring the intersection of minimalism and haute couture.',
    client: 'Vogue Italia',
    challenge:
    'Balancing the bold visual statements of high fashion with the restraint of minimalist design principles, while maintaining reader engagement across a lengthy editorial feature.',
    solution:
    'We created a dynamic layout system that alternates between full-bleed imagery and generous white space, using typography as a visual element rather than just information delivery. Each spread was designed as a standalone composition while maintaining cohesive flow.',
    results: [
    'Featured as cover story',
    '1.2M social media impressions',
    'Nominated for SPD Excellence Award'],

    additionalImages: [
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_19ca09690-1763700065904.png",
      alt: 'Fashion editorial layout showing bold typography paired with striking fashion photography'
    }]

  }];


  const filteredProjects = activeFilter === 'All Products'
    ? projects
    : projects.filter((p) => p.category === activeFilter);

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="pt-32 pb-20 px-6 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="mb-16">
              <div className="h-12 bg-muted animate-pulse mb-4 w-64" />
              <div className="h-6 bg-muted animate-pulse w-96" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) =>
              <div key={i} className="space-y-4">
                  <div className="aspect-[4/5] bg-muted animate-pulse" />
                  <div className="h-6 bg-muted animate-pulse w-3/4" />
                  <div className="h-4 bg-muted animate-pulse w-1/2" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <>
      <div className="pt-32 pb-20 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-16 lg:mb-20">
            <h1 className="text-5xl lg:text-7xl font-headline font-bold text-primary mb-6">
              Product Categories
            </h1>
            <p className="text-lg lg:text-xl font-body text-text-secondary max-w-2xl">
              Whether you need a complete Structural, Power, or Electrical package for a residential or light commercial elevator with up to six stops, or you only need a part to keep your system running smoothly, we&apos;ve got you covered. Choose a complete package or pick the part you need, our stock and inventory are built for both.
            </p>
          </div>

          {/* Filter Bar */}
          <div className="mb-12 lg:mb-16">
            <FilterBar
              categories={categories}
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter} />

          </div>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
            {filteredProjects.map((project) =>
            <ProjectCard
              key={project.id}
              project={project}
              onClick={() => setSelectedProject(project)} />

            )}
          </div>

          {/* Empty State */}
          {filteredProjects.length === 0 &&
          <div className="text-center py-20">
              <Icon
              name="FolderOpenIcon"
              size={48}
              className="mx-auto mb-4 text-muted-foreground" />

              <p className="text-lg font-body text-text-secondary">
                No projects found in this category.
              </p>
            </div>
          }
        </div>
      </div>

      {/* Case Study Modal */}
      <CaseStudyModal
        project={selectedProject}
        onClose={() => setSelectedProject(null)} />

    </>);

};

export default PortfolioInteractive;